import { defineMessages } from 'react-intl';

import { pluralTerm } from '../../lib/helpers/term.helper';
import { SportTermLookup, SportTerminology } from '../../lib/types';

export const scope = 'app.components.ReportCardAccess.availability';

// Counts arrive with their noun already pluralized ({players}, {contacts}, …) through `pluralize`.
export const getMessages = (termLookup: SportTermLookup) => {
  const athleteTerm = termLookup[SportTerminology.ATHLETE].toLowerCase();
  const athletesTerm = pluralTerm(athleteTerm);
  return defineMessages({
    titlePanel: { id: `${scope}.titlePanel`, defaultMessage: 'Report cards' },
    msgReleasedOn: { id: `${scope}.msgReleasedOn`, defaultMessage: 'First sent {date}' },
    msgNotReleased: { id: `${scope}.msgNotReleased`, defaultMessage: 'Not sent yet' },
    lblAgeGroupRevoked: { id: `${scope}.lblAgeGroupRevoked`, defaultMessage: 'Access turned off {date}' },
    msgAgeGroupRevokedExplained: {
      id: `${scope}.msgAgeGroupRevokedExplained`,
      defaultMessage: "Parents and anyone with a link can't open a report card in this age group, including links already sent. Staff still can.",
    },
    lblStatusCount: { id: `${scope}.lblStatusCount`, defaultMessage: '{status} · {count}' },
    msgNoPlayers: { id: `${scope}.msgNoPlayers`, defaultMessage: `No ${athletesTerm} in this age group yet.` },
    msgReadyConcluded: { id: `${scope}.msgReadyConcluded`, defaultMessage: '{count} concluded {players} ready to send.' },
    msgNotConcludedIncluded: {
      id: `${scope}.msgNotConcludedIncluded`,
      defaultMessage: "{count} {players} with a report card {haveNot} concluded; sends include them.",
    },
    msgNotConcludedExcluded: {
      id: `${scope}.msgNotConcludedExcluded`,
      defaultMessage: "{count} {players} with a report card {haveNot} concluded; they're sent one only if you include them.",
    },
    msgNothingWaiting: { id: `${scope}.msgNothingWaiting`, defaultMessage: `No ${athleteTerm} with a report card is waiting to be sent one.` },
    errAvailability: {
      id: `${scope}.errAvailability`,
      defaultMessage: "Couldn't load report card status for this age group. Refresh to try again.",
    },

    btnSend: { id: `${scope}.btnSend`, defaultMessage: 'Send report cards…' },
    btnRevokeAgeGroup: { id: `${scope}.btnRevokeAgeGroup`, defaultMessage: 'Revoke access for age group' },
    btnReEnableAgeGroup: { id: `${scope}.btnReEnableAgeGroup`, defaultMessage: 'Re-enable access' },
    btnCancel: { id: `${scope}.btnCancel`, defaultMessage: 'Cancel' },
    btnRetry: { id: `${scope}.btnRetry`, defaultMessage: 'Try again' },

    confirmRevokeTitle: { id: `${scope}.confirmRevokeTitle`, defaultMessage: 'Revoke report card access for {ageGroup}?' },
    confirmRevokeBody: {
      id: `${scope}.confirmRevokeBody`,
      defaultMessage: "Links already sent for {ageGroup} stop opening the report card, and signed-in parents lose access too. {count} {players} can open theirs right now. Nobody is notified, staff still see every report card, and no report cards can be sent until you re-enable access.",
    },
    confirmRevokeButton: { id: `${scope}.confirmRevokeButton`, defaultMessage: 'Revoke access' },
    confirmReEnableTitle: { id: `${scope}.confirmReEnableTitle`, defaultMessage: 'Re-enable report card access for {ageGroup}?' },
    confirmReEnableBody: {
      id: `${scope}.confirmReEnableBody`,
      defaultMessage: `Links already sent for {ageGroup} open the report card again for {count} {players}. Any ${athleteTerm} whose access was revoked on their own stays revoked, and nobody is notified.`,
    },
    confirmReEnableButton: { id: `${scope}.confirmReEnableButton`, defaultMessage: 'Re-enable access' },
    msgAgeGroupRevoked: { id: `${scope}.msgAgeGroupRevoked`, defaultMessage: 'Report card access revoked for {ageGroup}.' },
    msgAgeGroupReEnabled: { id: `${scope}.msgAgeGroupReEnabled`, defaultMessage: 'Report card access re-enabled for {ageGroup}.' },
    errRevokeFailed: { id: `${scope}.errRevokeFailed`, defaultMessage: "Report card access for {ageGroup} wasn't revoked. Please try again." },
    errRevokeRefused: { id: `${scope}.errRevokeRefused`, defaultMessage: "Report card access for {ageGroup} wasn't revoked: {reason}" },
    errReEnableFailed: { id: `${scope}.errReEnableFailed`, defaultMessage: "Report card access for {ageGroup} wasn't re-enabled. Please try again." },
    errReEnableRefused: { id: `${scope}.errReEnableRefused`, defaultMessage: "Report card access for {ageGroup} wasn't re-enabled: {reason}" },

    titleSend: { id: `${scope}.titleSend`, defaultMessage: 'Send report cards to {ageGroup}' },
    lblIncludeNonConcluded: { id: `${scope}.lblIncludeNonConcluded`, defaultMessage: `Include ${athletesTerm} who haven't concluded` },
    captionSourceAgeGroup: { id: `${scope}.captionSourceAgeGroup`, defaultMessage: 'Chosen for this age group.' },
    captionSourceAssessment: { id: `${scope}.captionSourceAssessment`, defaultMessage: "Following the assessment's default." },
    captionSourceAssociation: { id: `${scope}.captionSourceAssociation`, defaultMessage: "Following the association's default." },
    captionSourceDefault: { id: `${scope}.captionSourceDefault`, defaultMessage: `Following the standard setting: concluded ${athletesTerm} only.` },
    btnUseDefault: { id: `${scope}.btnUseDefault`, defaultMessage: 'Use the default' },
    errSettingFailed: {
      id: `${scope}.errSettingFailed`,
      defaultMessage: "The choice for {ageGroup} wasn't saved, so it's back to what it was. Please try again.",
    },
    errSettingRefused: { id: `${scope}.errSettingRefused`, defaultMessage: "The choice for {ageGroup} wasn't saved: {reason}" },

    msgPreviewLoading: { id: `${scope}.msgPreviewLoading`, defaultMessage: 'Checking who would be sent a report card…' },
    errPreview: {
      id: `${scope}.errPreview`,
      defaultMessage: "Couldn't check who would be sent a report card, so sending is paused.",
    },
    msgPreviewPlayers: { id: `${scope}.msgPreviewPlayers`, defaultMessage: '{count} {players} will be sent their report card.' },
    msgPreviewNotified: {
      id: `${scope}.msgPreviewNotified`,
      defaultMessage: '{count} {contacts} will be notified ({emailCount} {emails}, {smsCount} {texts}).',
    },
    msgPreviewAccess: {
      id: `${scope}.msgPreviewAccess`,
      defaultMessage: "{count} {contacts} will be able to open it, including any who don't get email or texts.",
    },
    msgPreviewUnreachable: {
      id: `${scope}.msgPreviewUnreachable`,
      defaultMessage: '{count} {players} {have} no contact who can be emailed or texted. Their report card is still released, but nobody is told.',
    },
    lblSkipped: { id: `${scope}.lblSkipped`, defaultMessage: 'Not included:' },
    skippedNotConcluded: { id: `${scope}.skippedNotConcluded`, defaultMessage: "{count} {players} who haven't concluded" },
    skippedNoData: { id: `${scope}.skippedNoData`, defaultMessage: '{count} {players} without a report card' },
    skippedNotReleased: { id: `${scope}.skippedNotReleased`, defaultMessage: '{count} {players} not sent one yet' },
    skippedRevoked: { id: `${scope}.skippedRevoked`, defaultMessage: '{count} {players} whose access is revoked' },
    skippedAlreadyReleased: { id: `${scope}.skippedAlreadyReleased`, defaultMessage: '{count} {players} already sent theirs' },
    skippedAgeGroupRevoked: { id: `${scope}.skippedAgeGroupRevoked`, defaultMessage: '{count} {players} in an age group with access turned off' },

    msgNothingNotConcluded: {
      id: `${scope}.msgNothingNotConcluded`,
      defaultMessage: `No concluded ${athletesTerm} are waiting for a report card. {count} {players} who haven't concluded {have} one; include them above to send to them.`,
    },
    msgNothingAllSent: {
      id: `${scope}.msgNothingAllSent`,
      defaultMessage: `Every ${athleteTerm} with a report card has already been sent theirs. To send one again, use Resend in that ${athleteTerm}'s report card actions.`,
    },
    msgNothingNoData: { id: `${scope}.msgNothingNoData`, defaultMessage: `No ${athleteTerm} in this age group has a report card yet.` },
    msgNothing: { id: `${scope}.msgNothing`, defaultMessage: `No ${athleteTerm} would be sent a report card.` },
    msgSendRevoked: {
      id: `${scope}.msgSendRevoked`,
      defaultMessage: "Report card access is turned off for {ageGroup}, so report cards can't be sent. Re-enabling lets links already sent open the report card again; then you can send.",
    },

    btnConfirmSend: { id: `${scope}.btnConfirmSend`, defaultMessage: 'Send to {playerCount} {players} · {contactCount} {contacts}' },
    btnSendPending: { id: `${scope}.btnSendPending`, defaultMessage: 'Send report cards' },
    btnNothingToSend: { id: `${scope}.btnNothingToSend`, defaultMessage: 'Nothing to send' },
    msgSent: {
      id: `${scope}.msgSent`,
      defaultMessage: 'Report cards sent for {ageGroup}: {playerCount} {players}, {count} {contacts} notified ({emailCount} {emails}, {smsCount} {texts}).',
    },
    msgSentWithUnreachable: {
      id: `${scope}.msgSentWithUnreachable`,
      defaultMessage: 'Report cards sent for {ageGroup}: {playerCount} {players}, {count} {contacts} notified ({emailCount} {emails}, {smsCount} {texts}). {unreachableCount} {unreachablePlayers} {have} no contact who can be emailed or texted, so nobody was told about theirs.',
    },
    msgNothingSentAlreadySent: {
      id: `${scope}.msgNothingSentAlreadySent`,
      defaultMessage: 'No new report cards were sent for {ageGroup}: another send had just sent {count} {players} theirs.',
    },
    msgSomeSentElsewhere: {
      id: `${scope}.msgSomeSentElsewhere`,
      defaultMessage: "{count} {players} in {ageGroup} had just been sent theirs by another send, so they weren't sent it again.",
    },
    msgNothingSentNow: {
      id: `${scope}.msgNothingSentNow`,
      defaultMessage: 'No report cards were sent for {ageGroup}: none are ready to send now. Refresh to see the current status.',
    },
    errSendFailed: { id: `${scope}.errSendFailed`, defaultMessage: "Report cards for {ageGroup} weren't sent. Please try again." },
    errSendRefused: { id: `${scope}.errSendRefused`, defaultMessage: "Report cards for {ageGroup} weren't sent: {reason}" },
    errSendIncomplete: {
      id: `${scope}.errSendIncomplete`,
      defaultMessage: "{failed} of {total} {players} in {ageGroup} didn't get their report card to every contact. Send again to finish; nobody already reached is sent it twice.",
    },
  });
};
